<template>
	<div
		class="w-full border-b border-slate-200 pb-4 print:pb-2"
		v-auto-animate
	>
		<!-- Header -->
		<div class="flex items-center justify-between">
			<div class="flex items-center justify-between py-2">
				<span class="text-md text-primary-700 font-bold">
					{{ t('payment.dueToday') }}
				</span>
			</div>

			<span class="text-md font-medium text-slate-500">
				{{ formatPrice(totalDueToday) }}
			</span>
		</div>

		<!-- Content -->
		<div
			class="mt-4 space-y-6 pl-6 *:flex *:items-center *:justify-between *:text-sm *:font-medium *:text-slate-500 print:space-y-2"
		>
			<!-- OAP -->
			<SwipeableToggle
				v-if="oapPayment && wasOapInitiallyIncluded"
				v-model="plan.oapIncluded"
			>
				<div
					class="grid w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-4 overflow-hidden"
					:class="{ 'text-secondary-300! line-through': !plan.oapIncluded }"
				>
					<!-- Label -->
					<div class="flex items-center">
						<p
							class="text-primary-700 text-sm font-bold"
							:class="{ 'text-secondary-400! line-through': !plan.oapIncluded }"
						>
							{{ t('oap.oap') }}
						</p>
					</div>

					<!-- Price -->
					<div
						class="text-sm text-slate-500"
						:class="{ 'text-secondary-300! line-through': !plan.oapIncluded }"
					>
						{{ formatPrice(oapPayment) }}
					</div>
				</div>
			</SwipeableToggle>

			<!-- Everplans -->
			<SwipeableToggle
				v-if="authStore.isEverplansAuthorized && wasEverplansInitiallyIncluded"
				v-model="plan.everplansIncluded"
			>
				<div
					class="grid w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-4 overflow-hidden"
					:class="{
						'text-secondary-300! line-through': !plan.everplansIncluded,
					}"
				>
					<!-- Label -->
					<div class="flex items-center">
						<p
							class="text-primary-700 text-sm font-bold"
							:class="{
								'text-secondary-400! line-through': !plan.everplansIncluded,
							}"
						>
							Everplans
						</p>
					</div>

					<!-- Price -->
					<div
						class="text-sm text-slate-500"
						:class="{
							'text-secondary-300! line-through': !plan.everplansIncluded,
						}"
					>
						{{
							everplansPrice
								? formatPrice(everplansPrice)
								: t('reviewSlide.included')
						}}
					</div>
				</div>
			</SwipeableToggle>

			<!-- First Payment -->
			<div v-if="firstPayment">
				<span class="text-primary-700 font-bold">{{
					t('payment.paymentTitle', { label: 'First' })
				}}</span>
				<span>{{ formatPrice(firstPayment) }}</span>
			</div>

			<!-- Down Payment -->
			<div v-if="plan.insurance.downPayment">
				<span class="text-primary-700 font-bold">{{
					t('payment.downPayment')
				}}</span>
				<span>{{ formatPrice(plan.insurance.downPayment) }}</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import SwipeableToggle from '../common/SwipeableToggle.vue'
import Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

defineProps<{
	oapPayment: number
	firstPayment: number
	totalDueToday: number
	plan: Plan
	everplansPrice: number
	wasOapInitiallyIncluded: boolean
	wasEverplansInitiallyIncluded: boolean
}>()

const { t } = usePresentationLocale()
const { formatPrice } = useMerchandise()
</script>
